import API_CONFIG from '../config/api';

const isFirefox = () =>
  typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);

const isSafari = () =>
  typeof navigator !== 'undefined' &&
  /safari/i.test(navigator.userAgent) &&
  !/chrome|chromium|android/i.test(navigator.userAgent);

const canUseNativeSaveFilePicker = () =>
  typeof window.showSaveFilePicker === 'function' && !isFirefox();

export const extractEsignFilename = filePath => {
  if (!filePath) return '';
  const trimmed = String(filePath).trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    const pathname = trimmed.split('?')[0];
    return pathname.split('/').pop() || '';
  }
  return trimmed.split('/').pop() || trimmed;
};

// Extension → real MIME type, for attachment endpoints that always respond
// with `Content-Type: application/octet-stream` regardless of the actual file
// (e.g. Task/Bug attachment downloads, by API contract). A Blob's `type` — not
// the origin response's Content-Disposition — is what a browser checks when
// deciding whether to render a `blob:` URL inline or save it, so an
// octet-stream blob always downloads even when opened in a fresh tab. This
// lets the "View" flow reconstruct the blob with its real type first so
// previewable files (PDF, images) render inline instead.
const EXTENSION_MIME_TYPES = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  txt: 'text/plain',
  // Natively playable by the browser's own <video>/<audio> element — same
  // "reconstruct the real type so it renders inline instead of downloading"
  // reasoning as the image/pdf types above. Formats with no reliable native
  // browser decoder (avi, wmv, mkv, doc, xlsx, ppt, …) are left unmapped on
  // purpose — re-typing their blob wouldn't make a browser able to play or
  // render them.
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  webm: 'video/webm',
  ogv: 'video/ogg',
  mov: 'video/quicktime',
};

export const guessMimeTypeFromFileName = fileName => {
  const ext = String(fileName ?? '').split('.').pop()?.toLowerCase();
  return EXTENSION_MIME_TYPES[ext] ?? null;
};

// Re-wraps a downloaded attachment blob under its real MIME type (guessed from
// the file name) when the server sent a generic `application/octet-stream` —
// use before creating an object URL you intend to open inline (e.g. in a new
// tab), not before a `<a download>` save (which should keep octet-stream).
export const toViewableBlob = async (blob, fileName) => {
  const currentType = (blob?.type || '').toLowerCase();
  if (currentType && currentType !== 'application/octet-stream') return blob;
  const mimeType = guessMimeTypeFromFileName(fileName);
  if (!mimeType) return blob;
  const buffer = await blob.arrayBuffer();
  return new Blob([buffer], { type: mimeType });
};

export const parseContentDispositionFilename = disposition => {
  if (!disposition) return null;
  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }
  const quotedMatch = disposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1].trim();
  const unquotedMatch = disposition.match(/filename=([^;]+)/i);
  return unquotedMatch?.[1]?.trim() || null;
};

const EXCEL_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const ensureExcelDownloadBlob = async (data, contentTypeHeader) => {
  const headerType = contentTypeHeader?.split(';')[0]?.trim();
  const excelType =
    headerType && !headerType.includes('json') ? headerType : EXCEL_MIME_TYPE;

  if (data instanceof Blob) {
    const type = (data.type || '').toLowerCase();
    if (!type || type === 'application/octet-stream') {
      return new Blob([await data.arrayBuffer()], { type: excelType });
    }
    return data;
  }

  return new Blob([data ?? ''], { type: excelType });
};

export const sanitizeDownloadFilename = (name, fallback = 'download.pdf') => {
  const base = (name || fallback).replace(/[/\\?%*:|"<>]/g, '_').trim();
  if (!base) return fallback;
  return base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`;
};

export const sanitizeGenericFilename = (name, fallback = 'download') => {
  const base = (name || fallback).replace(/[/\\?%*:|"<>]/g, '_').trim();
  return base || fallback;
};

export const getAxiosBlobErrorMessage = async error => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    const text = await data.text();
    try {
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.error || 'Download failed';
    } catch {
      return text?.trim() || 'Download failed';
    }
  }
  return error?.response?.data?.message || error?.message || 'Download failed';
};

const normalizeDownloadBlob = async (blob, contentTypeHeader) => {
  if (!(blob instanceof Blob)) {
    return new Blob([blob ?? ''], {
      type:
        contentTypeHeader?.split(';')[0]?.trim() || 'application/octet-stream',
    });
  }

  const type = (blob.type || '').toLowerCase();
  if (type.includes('json')) {
    const text = await blob.text();
    try {
      const parsed = JSON.parse(text);
      throw new Error(parsed?.message || parsed?.error || 'Download failed');
    } catch (err) {
      if (err instanceof SyntaxError) {
        throw new Error('Download failed');
      }
      throw err;
    }
  }

  const peekType = type || (contentTypeHeader || '').toLowerCase();
  if (
    !peekType ||
    peekType === 'application/octet-stream' ||
    peekType === 'text/plain'
  ) {
    const head = await blob.slice(0, 256).text();
    const trimmed = head.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      const text = await blob.text();
      try {
        const parsed = JSON.parse(text);
        throw new Error(parsed?.message || parsed?.error || 'Download failed');
      } catch (err) {
        if (err instanceof SyntaxError) {
          throw new Error('Download failed');
        }
        throw err;
      }
    }
  }

  if (!blob.type && contentTypeHeader) {
    return new Blob([await blob.arrayBuffer()], {
      type: contentTypeHeader.split(';')[0].trim(),
    });
  }

  return blob;
};

export const resolveDownloadFilename = (
  response,
  fallback = 'download.xlsx'
) => {
  const headerName = parseContentDispositionFilename(
    response?.headers?.['content-disposition']
  );
  if (headerName) return headerName;

  const contentType = (response?.headers?.['content-type'] || '').toLowerCase();
  if (contentType.includes('csv') || contentType.includes('comma-separated')) {
    return fallback.replace(/\.[^.]+$/, '.csv');
  }
  if (
    contentType.includes('spreadsheet') ||
    contentType.includes('excel') ||
    contentType.includes('sheet')
  ) {
    return fallback.replace(/\.[^.]+$/, '.xlsx');
  }
  return fallback;
};

export const normalizeAxiosBlob = async response => {
  let blob = response?.data;
  if (!(blob instanceof Blob)) {
    blob = new Blob([blob ?? ''], {
      type:
        response?.headers?.['content-type']?.split(';')[0]?.trim() ||
        'application/octet-stream',
    });
  }

  const type = (blob.type || '').toLowerCase();
  if (
    type.includes('json') ||
    type === 'text/plain' ||
    type.startsWith('text/')
  ) {
    const text = await blob.text();
    let message = 'Download failed';
    try {
      const parsed = JSON.parse(text);
      message = parsed?.message || parsed?.error || message;
    } catch {
      if (text?.trim()) message = text.trim();
    }
    throw new Error(message);
  }

  if (!type || type === 'application/octet-stream') {
    return new Blob([await blob.arrayBuffer()], { type: 'application/pdf' });
  }

  return blob;
};

/**
 * Firefox/Safari open application/pdf in-tab instead of saving when using
 * <a download>. octet-stream forces the save dialog.
 */
const toDownloadableBlob = async blob => {
  const buffer = await blob.arrayBuffer();
  const type = (blob.type || '').toLowerCase();
  const forceBinary =
    isFirefox() ||
    isSafari() ||
    type === 'application/pdf' ||
    type === 'application/octet-stream';

  if (forceBinary) {
    return new Blob([buffer], { type: 'application/octet-stream' });
  }

  return new Blob([buffer], { type: blob.type || 'application/octet-stream' });
};

const clickDownloadLink = link => {
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    })
  );
};

const triggerAnchorDownload = (blobUrl, safeName, containerEl) => {
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', safeName);
  link.rel = 'noopener';
  link.style.display = 'none';

  if (isFirefox()) {
    link.dataset.downloadurl = [
      'application/octet-stream',
      safeName,
      blobUrl,
    ].join(':');
  }

  const container =
    containerEl && containerEl.appendChild ? containerEl : document.body;
  container.appendChild(link);
  if (typeof link.click === 'function') {
    link.click();
  } else {
    clickDownloadLink(link);
  }

  const revokeDelay = isFirefox() ? 60000 : 5000;
  window.setTimeout(() => {
    if (link.parentNode) {
      link.parentNode.removeChild(link);
    }
    URL.revokeObjectURL(blobUrl);
  }, revokeDelay);
};

export const triggerBrowserFileDownload = async (
  blob,
  filename,
  { containerEl } = {}
) => {
  const safeName = sanitizeDownloadFilename(filename);
  const downloadBlob = await toDownloadableBlob(blob);

  if (typeof window.navigator?.msSaveOrOpenBlob === 'function') {
    window.navigator.msSaveOrOpenBlob(downloadBlob, safeName);
    return;
  }

  if (canUseNativeSaveFilePicker()) {
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: safeName,
        types: [
          {
            description: 'PDF Document',
            accept: { 'application/pdf': ['.pdf'] },
          },
        ],
      });
      const writable = await handle.createWritable();
      await writable.write(downloadBlob);
      await writable.close();
      return;
    } catch (err) {
      if (err?.name === 'AbortError') return;
    }
  }

  const blobUrl = URL.createObjectURL(downloadBlob);
  triggerAnchorDownload(blobUrl, safeName, containerEl);
};

export const triggerGenericFileDownload = async (
  blob,
  filename,
  { containerEl } = {}
) => {
  const safeName = sanitizeGenericFilename(filename);
  const downloadBlob = await toDownloadableBlob(blob);

  if (typeof window.navigator?.msSaveOrOpenBlob === 'function') {
    window.navigator.msSaveOrOpenBlob(downloadBlob, safeName);
    return;
  }

  const blobUrl = URL.createObjectURL(downloadBlob);
  triggerAnchorDownload(blobUrl, safeName, containerEl);
};

export const downloadAxiosBlobResponse = async (
  response,
  fallbackFilename,
  { containerEl } = {}
) => {
  const headerName = parseContentDispositionFilename(
    response?.headers?.['content-disposition']
  );
  const blob = await normalizeAxiosBlob(response);
  await triggerBrowserFileDownload(blob, headerName || fallbackFilename, {
    containerEl,
  });
};

export const downloadAxiosFileResponse = async (
  response,
  fallbackFilename,
  { containerEl } = {}
) => {
  const blob = await normalizeDownloadBlob(
    response?.data,
    response?.headers?.['content-type']
  );
  const filename = resolveDownloadFilename(response, fallbackFilename);
  await triggerGenericFileDownload(blob, filename, { containerEl });
};

export const downloadEsignDocument = async ({
  filePath,
  fileName,
  file,
  client = API_CONFIG.client,
  containerEl,
}) => {
  if (file instanceof File || file instanceof Blob) {
    await triggerBrowserFileDownload(
      file,
      file.name || fileName || 'e-Sign Form.pdf',
      { containerEl }
    );
    return;
  }

  const filename = extractEsignFilename(filePath);
  if (!filename) {
    throw new Error('No file available to download');
  }

  const response = await client.get(
    API_CONFIG.ENDPOINTS.ASSETS.ESIGN_DOWNLOAD(filename),
    { responseType: 'blob' }
  );

  await downloadAxiosBlobResponse(
    response,
    fileName || filename || 'e-Sign Form.pdf',
    { containerEl }
  );
};
